import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  BaseSyntheticEvent,
  ChangeEvent,
  FocusEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import SelectApplicationCycle from '@/components/dashboard_components/SelectApplicationCycle';
import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import usePartner from '@/hooks/usePartner';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { ApplicationExportModel } from '@/models/ApplicationExport';
import { CountyProps } from '@/models/County';
import { PartnerProps } from '@/models/Partner';
import { GrowthAwardProgramIds, ProgramApiResponse } from '@/models/Program';
import { SelectOptionProps } from '@/models/SelectOption';
import { StateProps } from '@/models/State';

import { DialogTitleWithClose } from '../common/DialogTitleWithClose';
import { isEmail } from '@/utils/emailUtil';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { logError } from '@/utils/errorHandlerUtil';

function ExportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { allPrograms, allStates, allCounties } = useContext(ConfigContext);
  const { userCurrent } = useContext(UsersContext);
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { translatedText } = useContext(LocaleContext);
  const { setAlert } = useContext(SnackbarContext);
  const { getPartnersByProgram } = usePartner();

  const { applicationExport } = useApplication();
  const appTypeOptions = [
    { label: translatedText.dashboard.exportTypeMainApplications, value: 1 },
    { label: translatedText.dashboard.exportTypeStateQuestions, value: 2 },
    { label: translatedText.dashboard.exportTypeGrowthAward, value: 3 },
  ];

  const [hasErrors, setHasErrors] = useState<boolean>(true);
  const [postingDownload, setPostingDownload] = useState<boolean>(false);
  const [exportSuccessful, setExportSuccessful] = useState<boolean>(false);

  const [exportEmails, setExportEmails] = useState<string[]>([]);

  const [countiesInState, setCountiesInState] = useState<SelectOptionProps[]>([]);
  const [programOptions, setProgramOptions] = useState<SelectOptionProps[]>([]);
  const [stateOptions, setStateOptions] = useState<SelectOptionProps[]>();

  const [partnerOptions, setPartnerOptions] = useState<SelectOptionProps[]>([]);

  const [selectedStateOption, setSelectedStateOption] = useState<SelectOptionProps | null>(null);
  const [includeQuestionResponseData, setIncludeQuestionResponseData] = useState<boolean>(false);
  const [selectedProgramOption, setSelectedProgramOption] = useState<SelectOptionProps | null>(
    null
  );
  const [selectedApplicationTypeOption, setSelectedApplicationTypeOption] =
    useState<SelectOptionProps>({
      label: translatedText.dashboard.exportTypeMainApplications,
      value: 1,
    });
  const [selectedCountyOption, setSelectedCountyOption] = useState<SelectOptionProps | null>(null);
  const [selectedApplicationCycle, setSelectedApplicationCycle] = useState<ApplicationCycle | null>(
    null
  );

  useEffect(() => {
    if (allPrograms != null) {
      const mappedProgramOptions = allPrograms?.map((v: ProgramApiResponse) => {
        return { label: v.programName, value: v.id };
      });
      setProgramOptions(mappedProgramOptions);
    }
  }, [allPrograms]);

  useEffect(() => {
    setHasErrors(
      selectedProgramOption == null ||
        selectedApplicationTypeOption == null ||
        exportEmails == null ||
        exportEmails.length == 0
    );
  }, [selectedProgramOption, selectedApplicationTypeOption, exportEmails]);

  useEffect(() => {
    setSelectedApplicationTypeOption({
      label: translatedText.dashboard.exportTypeMainApplications,
      value: 1,
    }); // reset to default
  }, [selectedProgramOption, translatedText.dashboard.exportTypeMainApplications]);

  const fetchData = useCallback(
    async (programId: string | number, abortController: AbortController) => {
      const partnerData = await getPartnersByProgram(Number(programId), abortController);
      if (partnerData == null || partnerData.length == 0) {
        logError(`No partners found for program ID: ${programId}`);
        return;
      }

      const partnerSelectOptions = partnerData
        .filter((partner: PartnerProps) => partner.partnerName != null && partner.id != null)
        .map((partner: PartnerProps) => {
          return { label: partner.partnerName!, value: partner.id! };
        })
        .sort((a: SelectOptionProps, b: SelectOptionProps) => {
          if (a.label.toLowerCase() < b.label.toLowerCase()) {
            return -1;
          }
          if (a.label.toLowerCase() > b.label.toLowerCase()) {
            return 1;
          }
          return 0;
        });

      setPartnerOptions(partnerSelectOptions);
    },
    [getPartnersByProgram]
  );

  useEffect(() => {
    const controller = new AbortController();

    if (selectedProgramOption?.value != null)
      void fetchData(selectedProgramOption.value, controller);

    return () => controller.abort('Parameters updated. Data fetching aborted.');
  }, [fetchData, selectedProgramOption?.value]);

  useEffect(() => {
    const mappedStateOptions = allStates.map((v: StateProps) => {
      return { label: v.abbrev, value: v.id };
    });
    setStateOptions(mappedStateOptions);
  }, [allStates]);

  useEffect(() => {
    if (userCurrent?.email != null) setExportEmails([userCurrent.email]);
  }, [userCurrent?.email]);

  useEffect(() => {
    if (selectedStateOption?.value != null && allCounties != null) {
      findCounties(allCounties, selectedStateOption.value.toString());
    }
  }, [selectedStateOption?.value, allCounties]);

  function findCounties(counties: CountyProps[], stateId: string) {
    if (counties != null) {
      const c = counties
        .filter((county: CountyProps) => county.stateId == stateId)
        .map((county: CountyProps) => {
          return { label: county.name, value: county.id };
        });

      setCountiesInState(c);
    }
  }

  const handleYearChange = useCallback(
    (event: SelectChangeEvent) => {
      const year = event.target.value;

      let updatedApplicationCycle: ApplicationCycle | null = null;
      if (year != null) {
        updatedApplicationCycle =
          applicationCycles.find(
            (appCycle: ApplicationCycle) =>
              appCycle.year === Number(year) &&
              appCycle.programId === selectedProgramOption?.value &&
              appCycle.applicationTypeId === selectedApplicationTypeOption.value
          ) ?? null;
      }

      setSelectedApplicationCycle(updatedApplicationCycle);
    },
    [applicationCycles, selectedApplicationTypeOption.value, selectedProgramOption?.value]
  );

  const selectedPartners: SelectOptionProps[] = useMemo(
    () =>
      partnerOptions != null
        ? partnerOptions.filter((partnerOption: SelectOptionProps) => partnerOption.selected)
        : [],
    [partnerOptions]
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithClose onClose={onClose}>
        {translatedText.dashboard.exportModalTitle}
      </DialogTitleWithClose>
      <DialogContent>
        <Typography variant='h3'>{translatedText.dashboard.exportRequiredParams}</Typography>

        <Autocomplete
          size='small'
          disabled={postingDownload}
          options={programOptions != null ? programOptions : []}
          value={selectedProgramOption}
          onChange={(_: BaseSyntheticEvent, newValue: SelectOptionProps | null) => {
            setSelectedProgramOption(newValue);
          }}
          renderInput={(params) => (
            <TextField {...params} label={translatedText.dashboard.exportSelectProgram} />
          )}
        />

        {selectedProgramOption?.value != null &&
          GrowthAwardProgramIds.includes(Number(selectedProgramOption.value)) && (
            <Autocomplete
              size='small'
              disabled={postingDownload}
              options={appTypeOptions != null ? appTypeOptions : []}
              value={selectedApplicationTypeOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translatedText.dashboard.exportSelectApplicationType}
                />
              )}
              onChange={(_: BaseSyntheticEvent, newValue: SelectOptionProps | null) => {
                if (newValue == null) return;
                setSelectedApplicationTypeOption(newValue);
              }}
            />
          )}

        <Autocomplete
          size='small'
          id='destination-email'
          disabled={postingDownload}
          multiple
          freeSolo
          options={[]}
          value={exportEmails}
          getOptionLabel={(option) => option}
          onChange={(_: BaseSyntheticEvent, newValue: string[] | null) => {
            if (newValue != null && exportEmails.length < 12) {
              setExportEmails(newValue);
            }
          }}
          autoSave='true'
          clearOnBlur={true}
          onBlur={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            // check if email is already in the list
            const addedEmail: string = event.currentTarget.value.trim();
            if (addedEmail != null && addedEmail != '') {
              if (!isEmail(addedEmail))
                setAlert({
                  message: translatedText.dashboard.exportInvalidEmailAddress(addedEmail),
                  severity: 'error',
                });
              else if (exportEmails.includes(addedEmail))
                setAlert({
                  message: translatedText.dashboard.exportEmailAlreadyAdded(addedEmail),
                  severity: 'warning',
                });
              else if (exportEmails.length >= 12)
                setAlert({
                  message: translatedText.dashboard.exportMaxEmailsWarning,
                  severity: 'warning',
                });
              else setExportEmails([...exportEmails, addedEmail]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant='outlined'
              label={translatedText.dashboard.exportEmailLabel}
              placeholder={translatedText.dashboard.exportEmailPlaceholder}
            />
          )}
        />

        {exportEmails.length >= 12 && (
          <Alert severity='warning'>{translatedText.dashboard.exportMaxEmailsWarning}</Alert>
        )}

        <SelectApplicationCycle
          disabled={postingDownload}
          onChange={handleYearChange}
          onlyShowAppCyclesWithApplications
        />

        <Typography variant='h3' sx={{ marginTop: '16px' }}>
          {translatedText.dashboard.exportOptionalParams}
        </Typography>

        <Autocomplete
          disabled={postingDownload}
          size='small'
          options={stateOptions != null ? stateOptions : []}
          value={selectedStateOption != null ? selectedStateOption : null}
          onChange={(_: BaseSyntheticEvent, val: SelectOptionProps | null) => {
            setSelectedStateOption(val);
            setSelectedCountyOption(null);
          }}
          renderInput={(params) => (
            <TextField {...params} label={translatedText.dashboard.exportSelectState} />
          )}
        />

        <Autocomplete
          disabled={postingDownload}
          size='small'
          options={countiesInState != null ? countiesInState : []}
          value={selectedCountyOption != null ? selectedCountyOption : null}
          onChange={(_: BaseSyntheticEvent, newValue: SelectOptionProps | null) =>
            setSelectedCountyOption(newValue)
          }
          renderInput={(params) => (
            <TextField {...params} label={translatedText.dashboard.exportSelectCounty} />
          )}
        />

        <Autocomplete
          size='small'
          id='partner-select'
          disabled={selectedProgramOption == null || postingDownload}
          multiple
          disableCloseOnSelect
          options={partnerOptions != null ? partnerOptions : []}
          value={selectedPartners}
          onChange={(_: BaseSyntheticEvent, newValue: SelectOptionProps[]) => {
            setPartnerOptions(
              partnerOptions.map((partnerOption: SelectOptionProps) => {
                return {
                  ...partnerOption,
                  selected: newValue.some(
                    (selectedPartner: SelectOptionProps) =>
                      selectedPartner.value === partnerOption.value
                  ),
                };
              })
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label={translatedText.dashboard.exportSelectPartner} />
          )}
        />

        <FormControlLabel
          control={
            <Checkbox
              disabled={selectedProgramOption == null}
              checked={includeQuestionResponseData}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setIncludeQuestionResponseData(event.target.checked);
              }}
            />
          }
          label={translatedText.dashboard.exportIncludeQuestionResponses}
        />
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
            }}
          >
            <Button
              disabled={hasErrors || postingDownload}
              variant='primary'
              size='large'
              onClick={() => downloadCsv()}
            >
              {postingDownload ? (
                <span>{translatedText.dashboard.exportGenerating}</span>
              ) : (
                translatedText.dashboard.exportGenerateCsv
              )}
            </Button>

            {exportSuccessful && (
              <Button
                type='button'
                disabled={hasErrors || postingDownload}
                variant='secondary'
                size='large'
                onClick={onClose}
              >
                {translatedText.common.buttonClose}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );

  async function downloadCsv() {
    setPostingDownload(true);
    if (!hasErrors) {
      const requestBody: ApplicationExportModel = {
        programId:
          selectedProgramOption?.value != null ? Number(selectedProgramOption.value) : undefined,
        applicationTypeId: Number(selectedApplicationTypeOption.value),
        email: exportEmails.join(';'),
        year: selectedApplicationCycle?.year,
        stateId: selectedStateOption?.value != null ? selectedStateOption.value.toString() : null,
        countyId:
          selectedStateOption != null &&
          selectedCountyOption?.value != null &&
          selectedCountyOption?.value != 'x'
            ? selectedCountyOption.value.toString()
            : null,
        includeQuestionResponses: includeQuestionResponseData,
        partnerIds:
          selectedPartners != null
            ? selectedPartners.map((partner: SelectOptionProps) => Number(partner.value))
            : null,
      };

      try {
        await applicationExport(requestBody);
        setExportSuccessful(true);
        setAlert({
          message: translatedText.dashboard.exportSuccessMessage,
          severity: 'success',
        });
      } catch {
        setExportSuccessful(false);
        setAlert({
          message: translatedText.dashboard.exportErrorMessage,
          severity: 'error',
        });
      } finally {
        setPostingDownload(false);
      }
    }
  }
}

export default ExportModal;
